import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, BellRing, CalendarCheck, Info, LayoutDashboard } from "lucide-react";
import { api, ApiError } from "@/lib/api-client";
import { useAppStore } from "@/store/app-store";
import type { User } from "@/types/domain";
import { Button } from "@/components/ui/Button";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { TempleMark, Wordmark } from "@/components/ui/TempleMark";

interface LoginValues { email: string; password: string }

const highlights = [
  { icon: LayoutDashboard, title: "One place for your property", body: "Bookings, rooms and guests together." },
  { icon: BellRing, title: "Never miss a booking", body: "Instant alerts with time to confirm." },
  { icon: CalendarCheck, title: "Run your stay with ease", body: "Check-ins, payments and reports." },
];

function loginErrorMessage(error: Error) {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 400) return "That email and password don't match. Check them and try again.";
    if (error.status === 403) return "This account has been disabled. Contact your administrator.";
    if (error.status >= 500) return "We couldn't sign you in right now. Please try again in a moment.";
    return error.message;
  }
  return "Can't reach the server. Check your internet connection and try again.";
}

export function LoginPage({ onShowWelcome }: { onShowWelcome?: () => void }) {
  const setSession = useAppStore((state) => state.setSession);
  const { register, handleSubmit, formState: { errors } } = useForm<LoginValues>({
    defaultValues: { email: "", password: "" },
  });
  const login = useMutation({
    mutationFn: (values: LoginValues) => api<{ token: string; user: User }>("/auth/login", { method: "POST", body: values }),
    onSuccess: ({ token, user }) => setSession(token, user),
  });

  return (
    <main className="login-page">
      <section className="login-visual" aria-label="About Darshan">
        <div className="login-visual__brand"><TempleMark size={36} /><Wordmark /></div>
        <div className="login-visual__content">
          <h1>Manage your stay, effortlessly.</h1>
          <p>Darshan brings your property, bookings and guests into one calm workspace.</p>
          <ul className="login-highlights">
            {highlights.map(({ icon: Icon, title, body }) => (
              <li key={title}><span><Icon size={18} /></span><div><strong>{title}</strong><small>{body}</small></div></li>
            ))}
          </ul>
        </div>
      </section>

      <section className="login-panel">
        <header className="login-hero">
          <span className="login-hero__mark"><TempleMark size={36} /></span>
          <div>
            <h2>Sign in</h2>
            <p>Welcome back. Your bookings are waiting.</p>
          </div>
        </header>

        <form className="login-form" noValidate onSubmit={handleSubmit((values) => login.mutate(values))}>
          {login.error && (
            <div className="form-alert" role="alert"><AlertCircle size={18} /><span>{loginErrorMessage(login.error)}</span></div>
          )}

          <div className="field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email" type="email" autoComplete="username" inputMode="email" enterKeyHint="next"
              aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "login-email-error" : undefined}
              {...register("email", { required: "Enter your email address" })}
            />
            {errors.email && <span className="field__error" id="login-email-error"><AlertCircle size={14} />{errors.email.message}</span>}
          </div>

          <div className="field">
            <label htmlFor="login-password">Password</label>
            <PasswordInput
              id="login-password" autoComplete="current-password" enterKeyHint="go"
              aria-invalid={Boolean(errors.password)} aria-describedby={errors.password ? "login-password-error" : undefined}
              {...register("password", { required: "Enter your password" })}
            />
            {errors.password && <span className="field__error" id="login-password-error"><AlertCircle size={14} />{errors.password.message}</span>}
          </div>

          <Button type="submit" className="login-form__submit" loading={login.isPending}>Sign in</Button>
          <p className="login-help">Need access? Contact your property administrator.</p>

          {onShowWelcome && (
            <button type="button" className="login-about" onClick={onShowWelcome}><Info size={16} /> About Darshan</button>
          )}
        </form>
      </section>
    </main>
  );
}
